import { ACTION } from '@talos/kyoko';
import { SelectNavigationSidebarDisplayMode } from '../components/Showcase/CustomRenderer/SelectNavigationSidebarDisplayMode';
import type { ShowcaseSpec } from '../components/Showcase/types';

/** Showcase names, sorted by order of appearance, most recent first */
export const ShowcaseName = {
  v132Mobile: 'v13.2.0 Mobile',
  v130Intel: 'v13.0.0 Intel',
  v130AnalyticsInTrading: 'v13.0 Analytics in Trading',
  v13PmsPeNavigation: 'v13.0.0 PMS and PE navigation',
  v100PriceGrid: 'v10.0.0 Price Grid',
  v255UpdatedNavigationSidebar: 'v2.55.0 Updated navigation sidebar',
  v256TradingSpaces: 'v2.56.0 Trading spaces',
} as const;
export type ShowcaseName = (typeof ShowcaseName)[keyof typeof ShowcaseName];

export const showcases: Record<ShowcaseName, ShowcaseSpec> = {
  [ShowcaseName.v132Mobile]: {
    releasedInVersion: '13.2.0',
    width: '640px',
    height: '820px',
    title: 'Mobile',
    requiredFeatureFlag: 'enableMobileLayout',
    steps: [
      {
        title: 'Overview',
        imageDark: '/assets/showcase/mobile-dark-1.png',
        imageLight: '/assets/showcase/mobile-light-1.png',
        description: `Access the Talos trading experience from your mobile device.

- Use the same layout configuration, including market cards and symbol favorites, from desktop and mobile.
- Configure a Space to be selected by default when you open Talos on mobile.
- Place and manage orders, and review positions, balances, credit, and trades.`,
      },
      {
        title: 'Market View',
        imageDark: '/assets/showcase/mobile-dark-2.png',
        imageLight: '/assets/showcase/mobile-light-2.png',
        description: `Use Market View to follow live prices and quickly find the instruments and markets you want to trade.

- Build a mobile watchlist for the symbols you follow.
- Use market cards for an at-a-glance view of current market activity.
- Open a symbol deep dive for more detailed market information.`,
      },
      {
        title: 'Order Form',
        imageDark: '/assets/showcase/mobile-dark-3.png',
        imageLight: '/assets/showcase/mobile-light-3.png',
        description: `Access the full Order Form to place and modify orders, with all available strategies and parameters.

Users need the **Mobile Trader** role to place orders from a mobile device. This role can be managed independently from desktop trading permissions.`,
      },
      {
        title: 'Orders',
        imageDark: '/assets/showcase/mobile-dark-4.png',
        imageLight: '/assets/showcase/mobile-light-4.png',
        description: `Monitor your open orders and review their current status from the Orders view.

You can modify or cancel open orders directly from your mobile device.`,
      },
      {
        title: 'Positions, Balances, Credit, Trades and More',
        imageDark: '/assets/showcase/mobile-dark-5.png',
        imageLight: '/assets/showcase/mobile-light-5.png',
        description: `Use the mobile navigation to review your Positions, Balances, Credit, Trades, and other trading activity.`,
      },
      {
        title: 'Restricted Mobile Trading',
        imageDark: '/assets/showcase/mobile-dark-6.png',
        imageLight: '/assets/showcase/mobile-light-6.png',
        description: `Trading via Mobile can be restricted so that traders can be permissioned to trade via desktop but not mobile.

Access is controlled through the **Mobile Trader** role.

Please contact Client Services to enable this feature.`,
      },
    ],
  },
  [ShowcaseName.v130Intel]: {
    releasedInVersion: '13.0.0',
    width: '640px',
    height: '600px',
    title: 'Intel',
    requiredFeatureFlag: 'enableSignals',
    requiredPermission: ACTION.VIEW_INTEL,
    steps: [
      {
        targetSelector: '[data-testid="sidebar-nav-item"][href="/intel"]',
        title: 'Introducing Intel',
        imageDark: '/assets/showcase/intel-1.png',
        imageLight: '/assets/showcase/intel-1.png',
        description: `Intel gives you a single place to track crypto data, from on-chain metrics and exchange flows, to prices and performance and funding rates, all in customizable dashboards.

- Single and Aggregated Asset metrics for detailed insights.
- Identify trends, the biggest price movers and compare performance across assets with relative performance charts and asset leaderboards.
- Dive into funding rates across assets and exchanges.`,
      },
      {
        targetSelector: '[data-testid="relative-performance-panel"]',
        title: 'Compare metrics and performance across assets',
        imageDark: '/assets/showcase/intel-2.png',
        imageLight: '/assets/showcase/intel-2.png',
        description: `View and compare prices and key metrics across multiple assets at once.

- Track asset metrics side by side for different assets.
- Compare relative performance across assets over custom time ranges.
- See relative changes as well as absolute values in one unified chart, customized to your needs.`,
      },
      {
        targetSelector: '[data-testid="historical-funding-rate-panel"]',
        title: 'Track funding rates your way',
        imageDark: '/assets/showcase/intel-4.png',
        imageLight: '/assets/showcase/intel-4.png',
        description: `View funding rates as a timeseries or in a blotter, in whichever format fits your workflow.

- Chart funding rate history at the asset level or by exchange, with moving averages.
- View the funding rate blotter as a list or matrix across exchanges.`,
      },
      {
        targetSelector: '#intel-sidebar-submenu-items',
        title: 'Build your own dashboard, or start from ours',
        imageDark: '/assets/showcase/intel-3.png',
        imageLight: '/assets/showcase/intel-3.png',
        description: `Intel ships with pre-configured Spaces (Overview, Leaderboard, Funding Rates, Stablecoins, Exchange Flows) so you can get started right away.

- Browse the pre-configured Spaces from the left nav.
- Create a new Space to build a custom dashboard from scratch.`,
      },
    ],
  },
  [ShowcaseName.v13PmsPeNavigation]: {
    releasedInVersion: '13.0.0',
    width: '800px',
    height: '720px',
    title: 'Portfolio navigation updates',
    requiredFeatureFlag: 'enablePMSPENavigationMigration',
    requiredPermission: [ACTION.VIEW_PORTFOLIO, ACTION.EDIT_OPERATIONS, ACTION.EDIT_REBALANCE, ACTION.EDIT_RESEARCH],
    steps: [
      {
        targetSelector: '[data-testid="sidebar-menu-top-items"]',
        title: 'Portfolio, Operations, and Research',
        imageDark: '/assets/showcase/pms-pe-navigation-dark.png',
        imageLight: '/assets/showcase/pms-pe-navigation-light.png',
        description: `We have restructured the old Portfolio and Portfolio Engineering menus into three new sections in the main navigation. Portfolio, Operations, and Research.

- Portfolio is for positions, risk, and portfolio construction
- Operations is for treasury, operational risk, and compliance
- Research is for research tools and data management`,
      },
      {
        targetSelector: '[data-testid="sidebar-nav-item"][href="/portfolio"]',
        title: 'Portfolio',
        imageDark: '/assets/showcase/pms-pe-navigation-portfolio-dark.png',
        imageLight: '/assets/showcase/pms-pe-navigation-portfolio-light.png',
        requiredPermission: [ACTION.VIEW_PORTFOLIO, ACTION.EDIT_REBALANCE],
        description: `Portfolio is now focused on positions, performance, risk, and portfolio construction. Portfolio construction includes tools for rebalancing previously found in Portfolio Engineering. Tools focused on portfolio operations have been moved its own section.`,
      },
      {
        targetSelector: '[data-testid="sidebar-nav-item"][href="/operations"]',
        title: 'Operations',
        imageDark: '/assets/showcase/pms-pe-navigation-operations-dark.png',
        imageLight: '/assets/showcase/pms-pe-navigation-operations-light.png',
        requiredPermission: [ACTION.VIEW_PORTFOLIO, ACTION.EDIT_OPERATIONS],
        description: `Operations is focused on treasury management, operational risk, and compliance. Tools focused on portfolio construction have been moved its own section in Portfolio.`,
      },
      {
        targetSelector: '[data-testid="sidebar-nav-item"][href="/research"]',
        title: 'Research',
        imageDark: '/assets/showcase/pms-pe-navigation-research-dark.png',
        imageLight: '/assets/showcase/pms-pe-navigation-research-light.png',
        requiredPermission: ACTION.EDIT_RESEARCH,
        description: `Research is now a top-level section. Use it for the back-tester, models, and research data management previously found in Portfolio Engineering.`,
      },
      {
        targetSelector: '[data-testid="sidebar-nav-item"][href="/admin"]',
        title: 'Advanced Portfolio in Admin',
        imageDark: '/assets/showcase/pms-pe-navigation-admin-dark.png',
        imageLight: '/assets/showcase/pms-pe-navigation-admin-light.png',
        requiredPermission: [ACTION.EDIT_RESEARCH, ACTION.EDIT_REBALANCE, ACTION.EDIT_OPERATIONS],
        description: `Portfolio Engineering jobs and permissions (if applicable) now live under Admin, in the new Advanced Portfolio section.`,
      },
    ],
  },
  [ShowcaseName.v130AnalyticsInTrading]: {
    releasedInVersion: '13.0',
    title: 'Analytics in Trading',
    requiredFeatureFlag: 'enableAnalyticsInTrading',
    requiredPermission: ACTION.VIEW_ANALYTICS,
    steps: [
      {
        targetSelector: '[data-testid="sidebar-nav-item"][href="/trading"]',
        title: 'Analytics has moved to Trading',
        description: `Analytics is now available within Trading. Open Trading and select Analytics to access Post Trade analytics.`,
        imageDark: '/assets/showcase/analytics-in-trading-dark.png',
        imageLight: '/assets/showcase/analytics-in-trading-light.png',
      },
    ],
  },
  [ShowcaseName.v100PriceGrid]: {
    releasedInVersion: '10.0.0',
    width: '640px',
    height: '520px',
    title: 'Price Grid',
    requiredFeatureFlag: 'enablePriceGridV2',
    steps: [
      {
        targetSelector: '[data-testid="add-tab-button"]',
        title: 'Introducing Price Grid',
        imageDark: '/assets/showcase/price-grid-1-dark.png',
        imageLight: '/assets/showcase/price-grid-1-light.png',
        description: `The Price Grid offers a new way for users to view prices in the UI.

- Create a filtered view of symbols in the system, excluding Options and Multileg.
- View prices across multiple markets or aggregations at the same time.
- Prime Orders and RFQ from the grid, with Click to Trade functionality also available.`,
      },
      {
        targetSelector: '[data-testid="add-tab-button"]',
        title: 'Compare prices across multiple markets.',
        imageDark: '/assets/showcase/price-grid-2-dark.png',
        imageLight: '/assets/showcase/price-grid-2-light.png',
        imageCaption: 'Rates are for illustrative purposes only.',
        description: `Multiple Price columns can be added to the grid, displaying rates across selected markets or aggregations.

- Click the + Price column button to add additional columns.
- Rename the column and configure the selected markets or aggregations.
- Enable Best Bid/Offer highlighting, to highlight the best rate across all price columns.`,
      },
      {
        targetSelector: '[data-testid="add-tab-button"]',
        title: 'Create a customised view of Symbols.',
        imageDark: '/assets/showcase/price-grid-3-dark.png',
        imageLight: '/assets/showcase/price-grid-3-light.png',
        description: `The Price Grid allows you to filter down from a list of all symbols into a customised view that will update live as symbols change. For example, create a Price Grid for BTC-USD Futures, which will add and remove symbols as they are added and expire.

- Filter for all symbols for a certain currency, or by Base Pair, filtering for all symbols of the Base/Quote currency pair e.g. BTC-USD will show BTC-USD spot, all BTC-USD Perps, Futures, etc.
- Filter for your Favourite symbols.
- Filter by Product and/or Market.`,
      },
      {
        targetSelector: '[data-testid="add-tab-button"]',
        title: 'Multileg Price Grid',
        imageDark: '/assets/showcase/price-grid-4-dark.png',
        imageLight: '/assets/showcase/price-grid-4-light.png',
        description: `The Multileg Price Grid displays all multileg symbols in the system.

- Allows the user to view underlying leg prices along with the multileg price.
- Filter across favourites, by Multileg Product or Ccy/Base Pair.
- View underlying leg markets, with the option to hide the column if needed.`,
      },
    ],
  },
  [ShowcaseName.v256TradingSpaces]: {
    releasedInVersion: '2.56.0',
    width: '640px',
    height: '600px',
    title: 'Trading spaces',
    requiredFeatureFlag: 'enableTradingSpaces',
    steps: [
      {
        targetSelector: '[data-testid="sidebar-submenu-wrapper"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/flexible-ui',
        title: 'Introducing Trading Spaces',
        description: `We've added a new way for users to organise their trading screens. You can now:
  - Add multiple spaces with different content and layout
  - Share spaces to other users in your organization
  - Setup default spaces for new users`,
        imageDark: '/assets/showcase/spaces-dark-1.png',
        imageLight: '/assets/showcase/spaces-light-1.png',
      },
      {
        targetSelector: '[data-testid="new-space-button"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/flexible-ui',
        title: 'Adding Spaces',
        description: `You can add new spaces tailored for different types of trading. You can start from an existing template or create a copy of your current space.`,
        imageDark: '/assets/showcase/spaces-dark-2.png',
        imageLight: '/assets/showcase/spaces-light-2.png',
      },
      {
        targetSelector: '[data-testid="create-template-button"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/flexible-ui',
        title: 'Sharing Spaces',
        description: `You can share your space with other users in your organization by creating new templates and sharing them with specific user roles, user groups or individual users.`,
        imageDark: '/assets/showcase/spaces-dark-3.png',
        imageLight: '/assets/showcase/spaces-light-3.png',
      },
      {
        targetSelector: '[data-testid="sidebar-submenu-items"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/flexible-ui',
        title: 'Default Spaces',
        description: `Admin users can configure templates to be included as the default setup for new users. Users with the Spaces Admin role can also add default spaces for all users in the organization. Default spaces are configured to appear based on the user’s role or user group.`,
        imageDark: '/assets/showcase/spaces-dark-4.png',
        imageLight: '/assets/showcase/spaces-light-4.png',
      },
    ],
  },
  [ShowcaseName.v255UpdatedNavigationSidebar]: {
    releasedInVersion: '2.55.0',
    width: '640px',
    height: '520px',
    title: 'Updated Navigation Sidebar',
    steps: [
      {
        targetSelector: '[data-testid="sidebar-menu"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/navigating-trading-ui',
        title: 'Updates to Our Main Navigation',
        description: `We've updated the navigation to make moving between products easier and more space-efficient.
  - Admin pages are separate from Settings and are now located in the main menu
  - User settings are now located in the bottom user popout
`,
        customRenderer: SelectNavigationSidebarDisplayMode,
      },
      {
        targetSelector: '[data-testid="sidebar-nav-item"][href="/admin"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/navigating-trading-ui',
        title: 'Centralized Admin Settings',
        imageDark: '/assets/showcase/wn-sidebar-admin-dark.png',
        imageLight: '/assets/showcase/wn-sidebar-admin-light.png',
        description: `A new section for admin settings has been added to the main menu. It contains all the organization wide setup that were previously located in the Settings menu.`,
      },
      {
        targetSelector: '[data-testid="sidebar-user-profile-item"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/navigating-trading-ui',
        title: 'User Settings Relocated',
        imageDark: '/assets/showcase/wn-sidebar-user-settings-dark.png',
        imageLight: '/assets/showcase/wn-sidebar-user-settings-light.png',
        description: `All user-related settings and preferences are now located in the bottom user popout, making them easier to find and access.`,
      },
      {
        targetSelector: '[data-testid="sidebar-search-item"]',
        learnMoreUrl: 'https://kb.talostrading.com/principal-trading/navigating-trading-ui/power-search',
        title: 'Improved Power Search',
        imageDark: '/assets/showcase/wn-sidebar-power-search-dark.png',
        imageLight: '/assets/showcase/wn-sidebar-power-search-light.png',
        description: `You can now navigate to any page using the power search bar. Just type the name of the page you want to navigate to and press enter.`,
      },
    ],
  },
};
